import { assertEvent, assign, raise } from 'xstate';

import { type Predictions, type RadarPredictions } from '@sg/garnish';

import { type LocationGroups, type LocationSearchMachineAction } from '../../../types';
import { expandBoundsToMinimumRadius } from '../../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const raiseSearchBySelectedPredictionGeo: LocationSearchMachineAction = raise({
  type: 'SEARCH_BY_SELECTED_PREDICTION_GEO',
});

export const setPredictionsSearchQuery: LocationSearchMachineAction = assign(({ event }) => {
  assertEvent(event, 'SEARCH_FOR_PREDICTIONS');

  return { predictionsSearchQuery: event.searchString };
});

export const clearSearchPredictions: LocationSearchMachineAction = assign(() => ({
  predictionsSearchQuery: undefined,
  predictions: [],
  predictionLocations: undefined,
}));

export const clearBaseLocation: LocationSearchMachineAction = assign({
  baseLocation: () => undefined,
});

export const onSearchForPredictionsAndRestaurantsDone: LocationSearchMachineAction<PredictionsAndRestaurantsOutput> =
  assign((_, params) => ({
    predictions: params.predictions,
    predictionLocations: params.locations,
    activePredictionTab:
      params.predictions.length > 0
        ? ('location-prediction-tab.predictions' as const)
        : ('location-prediction-tab.locations' as const),
  }));

export const onSelectPredictionDone: LocationSearchMachineAction<SelectPredictionOutput> = assign(
  (_, params) => ({
    searchString: params?.description ?? '',
    baseLocation: getBaseLocationFromSelectPrediction(params),
  }),
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getBaseLocationFromSelectPrediction(output: SelectPredictionOutput) {
  const lat = output?.lat;
  const lng = output?.lng;

  if (!lat || !lng) return undefined;

  const originalBounds = output?.bounds;

  const bounds = originalBounds
    ? expandBoundsToMinimumRadius({
        bounds: originalBounds,
        centerLat: lat,
        centerLng: lng,
      })
    : undefined;

  return {
    latitude: lat,
    longitude: lng,
    name: output?.description ?? '',
    types: output?.types,
    bounds,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type PredictionsAndRestaurantsOutput = {
  predictions: Predictions | RadarPredictions;
  locations: LocationGroups;
};

type SelectPredictionOutput =
  | {
      description: string;
      lat: number;
      lng: number;
      types: readonly string[];
      bounds?: {
        southwest: { lat: number; lng: number };
        northeast: { lat: number; lng: number };
      };
    }
  | undefined;
